import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { APP_VERSION } from '../config/version';
import sgLogo from '../assets/logo/sg-logo.png';
import './Login.css';

/**
 * Admin Login Page
 *
 * Full-screen centered layout with SG branding.
 * Handles authentication and redirects to dashboard on success.
 */
const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Clear error when inputs change
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [username, password]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !password) {
      return;
    }

    setIsSubmitting(true);
    const success = await login(username.trim(), password);
    setIsSubmitting(false);

    if (success) {
      navigate('/admin/dashboard', { replace: true });
    }
  };

  // Show loading state while checking initial auth
  if (isLoading && !isSubmitting) {
    return (
      <div className="login-page">
        <div className="login-loading">
          <div className="login-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        {/* Logo */}
        <div className="login-logo">
          <img src={sgLogo} alt="Seraphina Grey" />
        </div>

        {/* Title */}
        <h1 className="login-title">Admin Portal</h1>
        <p className="login-subtitle">Sign in to manage proof sessions</p>

        {/* Error Message */}
        {error && (
          <div className="login-error" role="alert">
            <svg
              className="login-error-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              autoComplete="username"
              autoFocus
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              disabled={isSubmitting}
              required
            />
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isSubmitting || !username.trim() || !password}
          >
            {isSubmitting ? (
              <>
                <span className="login-button-spinner" />
                <span>Signing in...</span>
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="login-footer">
          Seraphina Grey Proofs System
          <span className="login-version">v{APP_VERSION}</span>
        </p>
      </div>
    </div>
  );
};

export default Login;
